/**
 * Procedure families — the one table behind every surface that thinks in the
 * office's terms ("recall exam", "scaling", "crown", "denture") instead of raw
 * CDA codes: the text/print exports (Breakdown/print/exportModel.js) and the
 * Breakdown Worksheet (BreakdownWorksheet/sampleData.js).
 *
 * Two grains, because carriers quote them at two grains:
 *
 *   FAMILY  the unit a carrier quotes COVERAGE for — "Scaling & polishing" is
 *           one percentage even though four codes sit under it.
 *   LINE    the finer question the paper PLAN INFO sheet asks — "Preventative
 *           scaling %", "Perio scaling %", "Polishing frequency" — each printed
 *           with one representative `templateCode`.
 *
 * A line is answered by whichever breakdown row fits it, tried in this order:
 *   1. `codes`     exact codes, best first. The order follows what the scrapers
 *                  actually ask for (clinics/breakdown_procedure_codes.py and
 *                  each carrier's InsuranceProvider.default_breakdown_procedure_codes),
 *                  so Sun Life's 23222, ProviderConnect's 23325 and the paper
 *                  form's 23322 all answer "composites on molars".
 *   2. `prefixes`  CDA code ranges from the fee guide — a plan that covers any
 *                  1111x code covers scaling.
 *   3. `names`     the PMS description, for office-private codes that sit
 *                  outside every range (01330 "ORAL HYGIENE INSTRUCT", 43400
 *                  "Periodontal Scaling And Root Planing", 80001 "Orthodontics").
 *                  This is not a carrier vocabulary: that is the backend's
 *                  ProcedureTermAlias / CarrierPlaybook.term_lexicon, which turns
 *                  what a rep SAID into a code before a row is ever stored.
 *
 * Categories are the backend's four COVERAGE_TYPES. A row's category is decided
 * server-side (clinics/services/breakdown_persistence.resolve_procedure_category)
 * and arrives as `coverage_type`; nothing here re-derives it. `defaultCategory`
 * is only what the worksheet shows before the carrier answers, and follows the
 * same ranges as `category_from_code_range` (0/1xxxx preventive; 27xxx, 5xxxx,
 * 6xxxx major; 8xxxx/933xx ortho; the rest basic; 43421 curated preventive).
 */
import dayjs from "dayjs";

import { classifyEligibility } from "./eligibility";

export const COVERAGE_CATEGORIES = ["Preventative", "Basic", "Major", "Orthodontics"];

const c = (code, name) => ({ code, name });

const FAMILY_DEFS = [
  { key: "exams", name: "Exams", defaultCategory: "Preventative", pool: "annual" },
  { key: "xrays", name: "X-rays", defaultCategory: "Preventative", pool: "annual" },
  { key: "hygiene", name: "Scaling & polishing", defaultCategory: "Preventative", pool: "annual" },
  { key: "fluoride", name: "Fluoride", defaultCategory: "Preventative", pool: "annual" },
  { key: "sealants", name: "Sealants", defaultCategory: "Preventative", pool: "annual" },
  { key: "ohi", name: "Oral hygiene instruction", defaultCategory: "Preventative", pool: "annual" },
  { key: "appliances", name: "Night guard", defaultCategory: "Preventative", pool: "annual" },
  { key: "restorative", name: "Fillings / Restorative", defaultCategory: "Basic", pool: "annual" },
  { key: "endo", name: "Endodontics", defaultCategory: "Basic", askBasicMajor: true, pool: "annual" },
  { key: "periosurg", name: "Perio Surgery", defaultCategory: "Basic", askBasicMajor: true, pool: "annual" },
  { key: "oralsurg", name: "Oral Surgery", defaultCategory: "Basic", askBasicMajor: true, pool: "annual" },
  { key: "crown", name: "Crown", defaultCategory: "Major", pool: "annual" },
  { key: "bridge", name: "Bridge", defaultCategory: "Major", pool: "annual" },
  { key: "denture", name: "Denture", defaultCategory: "Major", pool: "annual" },
  { key: "ortho", name: "Orthodontics", defaultCategory: "Orthodontics", isOrtho: true, pool: "ortho" },
  { key: "adjunctive", name: "Nitrous / adjunctive", defaultCategory: "Basic", pool: "annual" },
  { key: "implants", name: "Implants", defaultCategory: "Major", pool: "annual" },
];

// Scaling and root planing are quoted in time units; the widest row is the
// allowance. Codes below are listed smallest first so the exact-code tier keeps
// the paper form's 1-unit code as the representative when several are present.
const SCALING_CODES = [
  c("11111", "scaling, 1 unit"), c("11112", "scaling, 2 units"), c("11113", "scaling, 3 units"),
  c("11114", "scaling, 4 units"), c("11115", "scaling, 5 units"), c("11116", "scaling, 6 units"),
  c("11117", "scaling, half unit"),
];

export const PROCEDURE_LINES = [
  {
    // The recall exam alone: emergency, specific and new-patient exams carry
    // limits of their own (every Cleer profile showed them differing from the
    // recall's), so they are lines of their own below.
    key: "recall", label: "Recall exam", family: "exams", templateCode: "01202", frequencyRow: true,
    codes: [c("01202", "recall exam")],
    prefixes: ["01202"], names: /recall|periodic exam|previous patient/i,
  },
  {
    key: "emergencyExam", label: "Emergency exam", family: "exams", templateCode: "01205", frequencyRow: true,
    codes: [c("01205", "emergency exam")], prefixes: ["01205"], names: /emergency/i,
  },
  {
    key: "specificExam", label: "Specific exam", family: "exams", templateCode: "01204", frequencyRow: true,
    codes: [c("01204", "specific exam")], prefixes: ["01204"], names: /specific/i,
  },
  {
    key: "newPatientExam", label: "Limited exam, new patient", family: "exams", templateCode: "01201", frequencyRow: true,
    codes: [c("01201", "limited exam, new patient")], prefixes: ["01201"], names: /new patient/i,
  },
  {
    // The complete exam is coded by dentition, so the claimant's age decides
    // which of the three codes the carrier was really asked about.
    key: "exam", label: "Complete exam", family: "exams", templateCode: "01103", frequencyRow: true,
    codes: [c("01103", "complete exam, permanent dentition"), c("01102", "complete exam, mixed dentition"), c("01101", "complete exam, primary dentition")],
    byAge: [{ maxAge: 6, code: "01101" }, { maxAge: 12, code: "01102" }, { code: "01103" }],
    prefixes: ["011"], names: /comp(lete|rehensive)?\.?,? ?(oral )?exam|complete, (permanent|mixed|primary)|exam[^|]*(permanent|mixed|primary) dentition/i,
  },
  {
    key: "bitewings", label: "Bitewings", family: "xrays", templateCode: "02144", frequencyRow: true,
    codes: [c("02144", "bitewings, 4 images"), c("02143", "bitewings, 3 images"), c("02142", "bitewings, 2 images"), c("02141", "bitewing, 1 image")],
    prefixes: ["0214"], names: /bitewing|bite-wing/i, exclude: /complete series|full mouth/i,
  },
  {
    key: "fullSeries", label: "Full series", family: "xrays", templateCode: "02102",
    codes: [c("02102", "complete series, 16+ images"), c("02101", "complete series, 12+ images")],
    prefixes: ["0210"], names: /complete series|full[- ]mouth series|fmx|fms/i,
  },
  {
    key: "pan", label: "Pan", family: "xrays", templateCode: "02601", frequencyRow: true,
    codes: [c("02601", "panoramic x-ray")], prefixes: ["026"], names: /panoram|panorex/i,
  },
  {
    key: "polishing", label: "Polishing", family: "hygiene", templateCode: "11101", frequencyRow: true,
    codes: [c("11101", "polishing, 1 unit"), c("11107", "polishing, half unit")],
    prefixes: ["1110"], names: /polish|prophy/i,
  },
  {
    key: "scaling", label: "Scaling units", family: "hygiene", templateCode: "11111", pick: "widest",
    codes: SCALING_CODES, prefixes: ["1111"], names: /^scaling\b/i,
  },
  {
    key: "preventativeScaling", label: "Preventative scaling", family: "hygiene", templateCode: "11111", frequencyRow: true,
    codes: SCALING_CODES, prefixes: ["1111"], names: /^scaling\b/i,
  },
  {
    // The paper form says "perio scaling"; what every carrier list actually
    // checks is root planing (43421), and Manulife's manual entries use the
    // office code 43400 "Periodontal Scaling And Root Planing" for the same thing.
    key: "perioScaling", label: "Perio scaling / root planing", family: "hygiene", templateCode: "43421", pick: "widest",
    codes: [
      c("43421", "root planing, 1 unit"), c("43422", "root planing, 2 units"), c("43423", "root planing, 3 units"),
      c("43424", "root planing, 4 units"), c("43425", "root planing, 5 units"), c("43426", "root planing, 6 units"),
      c("43427", "root planing, half unit"), c("43400", "periodontal scaling & root planing"),
    ],
    prefixes: ["434"], names: /root planing|periodontal scaling|perio(dontal)? maint/i,
  },
  {
    key: "fluoride", label: "Fluoride", family: "fluoride", templateCode: "12113", frequencyRow: true,
    codes: [c("12113", "fluoride varnish"), c("12112", "fluoride gel/foam"), c("12111", "fluoride rinse"), c("12101", "topical fluoride")],
    prefixes: ["121"], names: /fl(u|ou)oride/i,
  },
  {
    key: "sealants", label: "Sealants", family: "sealants", templateCode: "13401",
    codes: [c("13401", "sealant, first tooth"), c("13409", "sealant, each additional tooth")],
    // 1340x only: 1341x is preventive resin, a line of its own.
    prefixes: ["1340"], names: /sealant|pit (and|&) fissure/i,
  },
  {
    key: "ohi", label: "Hygiene instruction", family: "ohi", templateCode: "13211",
    codes: [
      c("13211", "hygiene instruction, 1 unit"), c("13217", "hygiene instruction, half unit"), c("13212", "hygiene instruction, 2 units"),
      c("13219", "hygiene instruction, additional"), c("13231", "hygiene re-instruction"), c("13241", "hygiene instruction, audio-visual"),
      c("13200", "hygiene instruction"), c("01330", "hygiene instruction"),
    ],
    prefixes: ["132", "0133"], names: /hygiene instruct|oral hygiene|plaque control/i,
  },
  {
    key: "nightGuard", label: "Night guard", family: "appliances", templateCode: "14611",
    codes: [c("14611", "night guard, upper"), c("14612", "night guard, lower")],
    prefixes: ["146"], names: /bruxism|night ?guard|occlusal guard|appliances?, periodontal/i,
  },
  {
    // 2332x = tooth-coloured filling on a permanent molar (the paper form's 23322);
    // Sun Life/CDCP ask 23222 (the same question, non-bonded), ProviderConnect 23325.
    key: "molarComposites", label: "Composites on molars", family: "restorative", templateCode: "23322",
    codes: [
      c("23322", "composite, permanent molar, 2 surfaces"), c("23321", "composite, permanent molar, 1 surface"),
      c("23323", "composite, permanent molar, 3 surfaces"), c("23324", "composite, permanent molar, 4 surfaces"),
      c("23325", "composite, permanent molar, 5 surfaces"), c("23222", "tooth-coloured filling, permanent posterior, 2 surfaces"),
    ],
    prefixes: ["2332", "2322"],
    names: /(molar|posterior)[^|]*(composite|tooth[- ]?colou?red|bonded|white)|(composite|tooth[- ]?colou?red|bonded|white)[^|]*(molar|posterior)/i,
  },
  {
    key: "restorative", label: "Restorative", family: "restorative", templateCode: "23222",
    codes: [c("23222", "tooth-coloured filling, permanent posterior, 2 surfaces"), c("23322", "composite, permanent molar, 2 surfaces"), c("21211", "amalgam, 1 surface")],
    prefixes: ["21", "22", "23"], names: /restoration|filling|amalgam|composite/i, exclude: /crown|denture|bridge/i,
  },
  {
    key: "endodontics", label: "Endodontics", family: "endo", templateCode: "33111",
    codes: [
      c("33111", "root canal, 1 canal"), c("33121", "root canal, 2 canals"), c("33124", "root canal, 2 canals (calcified)"),
      c("33131", "root canal, 3 canals"), c("33141", "root canal, 4+ canals"),
    ],
    prefixes: ["33", "32", "34"], names: /root canal|endodont|pulp(otomy|ectomy)/i,
  },
  {
    key: "perioSurgery", label: "Perio surgery", family: "periosurg", templateCode: "42431",
    codes: [c("42431", "perio surgery, flap with osteoplasty"), c("42421", "perio surgery, flap with curettage")],
    prefixes: ["42"], names: /flap approach|gingivectomy|gingivoplasty|osseous|graft/i,
  },
  {
    key: "oralSurgery", label: "Oral surgery", family: "oralsurg", templateCode: "71101",
    codes: [c("71101", "extraction, uncomplicated"), c("72211", "extraction, impacted (soft tissue)")],
    prefixes: ["71", "72"], excludePrefixes: ["724"],
    names: /extraction|removals?,? (erupted|impact|residual|teeth|tooth)/i, exclude: /bridge|denture|prosthes|appliance/i,
  },
  {
    key: "crown", label: "Crown", family: "crown", templateCode: "27211",
    codes: [c("27211", "crown, porcelain fused to metal"), c("27201", "crown, porcelain/ceramic"), c("27401", "crown on partial-denture clasp")],
    prefixes: ["27"], names: /crown/i,
  },
  {
    // The paper form prints the complete denture; Sun Life's list asks the
    // cast partial (53201), so that is what usually answers this line.
    key: "denture", label: "Denture", family: "denture", templateCode: "51101",
    codes: [c("53201", "partial denture, cast, upper"), c("53202", "partial denture, cast, lower"), c("51101", "complete denture, upper"), c("51102", "complete denture, lower")],
    prefixes: ["51", "52", "53"], names: /denture/i, exclude: /crown|reline|rebase|repair|adjust/i,
  },
  {
    key: "reline", label: "Reline", family: "denture", templateCode: "56241",
    codes: [c("56241", "reline, partial denture (processed)"), c("56211", "reline, complete denture (direct)"), c("56221", "reline, partial denture (direct)")],
    prefixes: ["562", "563"], names: /reline|rebase/i,
  },
  {
    // 66xxx are bridge REPAIRS — a plan that pays to fix a bridge has not said it
    // pays for one, so they are left out of the range.
    key: "bridge", label: "Bridge", family: "bridge", templateCode: "67211",
    codes: [c("67211", "bridge retainer, porcelain fused to metal"), c("67201", "bridge retainer, porcelain"), c("62501", "bridge pontic, porcelain fused to metal"), c("62101", "bridge pontic, cast metal")],
    prefixes: ["62", "63", "64", "65", "67"], names: /bridge|pontic|retainer/i, exclude: /repair|remov|ortho/i,
  },
  {
    // 933xx are the ortho payment codes the portals answer; 8xxxx (lump sum /
    // appliance) usually come back unavailable, and 80001/88000 are office codes.
    key: "ortho", label: "Ortho", family: "ortho", templateCode: "93341",
    codes: [c("93341", "ortho predetermination"), c("93331", "ortho payment in progress"), c("80000", "orthodontics"), c("80001", "orthodontics"), c("88000", "orthodontics")],
    prefixes: ["933", "8"], names: /orthodont/i,
  },
  {
    key: "cbct", label: "CBCT", family: "xrays", templateCode: "07021",
    codes: [c("07021", "CBCT, 1 unit"), c("07011", "CBCT"), c("07031", "CBCT")],
    prefixes: ["070"], names: /cbct|cone beam/i,
  },
  {
    key: "preventiveResin", label: "Preventive resin", family: "sealants", templateCode: "13411",
    codes: [c("13411", "preventive resin restoration, first tooth"), c("13419", "preventive resin restoration, each additional tooth")],
    prefixes: ["1341"], names: /preventi(ve|ative) resin/i,
  },
  {
    key: "ridgePreservation", label: "Ridge preservation (PRF)", family: "oralsurg", templateCode: "72411",
    codes: [c("72411", "ridge preservation, first tooth")], prefixes: ["724"], names: /ridge preserv|platelet|\bprf\b/i,
  },
  {
    key: "implants", label: "Implants", family: "implants", templateCode: "79931",
    codes: [c("79931", "implant"), c("27205", "crown on implant"), c("02951", "implant guide radiograph")],
    prefixes: ["799"], names: /implant/i,
  },
  {
    key: "nitrous", label: "Nitrous", family: "adjunctive", templateCode: "92413",
    codes: [c("92413", "nitrous oxide"), c("92412", "nitrous oxide"), c("92414", "nitrous oxide"), c("92416", "nitrous oxide")],
    prefixes: ["924"], names: /nitrous/i,
  },
];

export const PROCEDURE_FAMILIES = FAMILY_DEFS.map((fam) => {
  const lines = PROCEDURE_LINES.filter((l) => l.family === fam.key);
  // The codes shown under a family are its lines' template codes — what staff
  // recognise the family by — not every code that can answer it.
  const codes = [];
  lines.forEach((l) => {
    if (!codes.some((x) => x.code === l.templateCode)) codes.push(c(l.templateCode, l.label));
  });
  return Object.assign({}, fam, { lines, codes });
});

export const lineByKey = {};
PROCEDURE_LINES.forEach((l) => { lineByKey[l.key] = l; });

export const familyByKey = {};
PROCEDURE_FAMILIES.forEach((f) => { familyByKey[f.key] = f; });

// ---- matching rows to lines ----------------------------------------------------

export function rowCode(row) {
  const det = (row && row.procedure_code_details) || {};
  return det.procedure_code != null ? String(det.procedure_code).trim() : "";
}

export function rowDescription(row) {
  const det = (row && row.procedure_code_details) || {};
  return det.description ? String(det.description) : "";
}

export function compareByCode(a, b) {
  return rowCode(a).localeCompare(rowCode(b), undefined, { numeric: true });
}

export function isCoveredRow(row) {
  if (!row || row.data_unavailable) return false;
  const pct = row.percentage_covered != null && row.percentage_covered !== "" ? Number(row.percentage_covered) : null;
  if (pct != null) return pct > 0;
  const state = classifyEligibility(row);
  return state === "eligible" || state === "cooldown";
}

/** Whole years old on `at` (default now), or null without a usable date. */
export function ageOn(dob, at) {
  if (!dob) return null;
  const born = dayjs(String(dob).split("T")[0]);
  const ref = dayjs(at || undefined);
  if (!born.isValid() || !ref.isValid()) return null;
  const years = ref.diff(born, "year");
  return years >= 0 && years < 130 ? years : null;
}

// Only real five-digit CDA codes take part in range matching: an office's
// "0110 Test" or "2601" must not stand in for the exam or the pan.
function isCdaCode(code) {
  return /^\d{5}$/.test(code);
}

function preferredCodes(line, opts) {
  const base = (line.codes || []).map((x) => x.code);
  const age = opts && opts.age != null && isFinite(Number(opts.age)) ? Number(opts.age) : null;
  if (age == null || !line.byAge) return base;
  const band = line.byAge.find((b) => b.maxAge == null || age <= b.maxAge);
  if (!band) return base;
  return [band.code].concat(base.filter((code) => code !== band.code));
}

function matchesName(line, row) {
  if (!line.names) return false;
  const text = rowDescription(row);
  if (!text || !line.names.test(text)) return false;
  return !(line.exclude && line.exclude.test(text));
}

function hitsIn(candidates, line, opts) {
  let hits = [];
  preferredCodes(line, opts).forEach((code) => {
    hits = hits.concat(candidates.filter((r) => rowCode(r) === code));
  });
  if (!hits.length) {
    // `excludePrefixes`: a sub-range that is a line of its own (72411 ridge
    // preservation inside oral surgery's 72xxx).
    const carvedOut = (code) => (line.excludePrefixes || []).some((x) => code.indexOf(x) === 0);
    (line.prefixes || []).forEach((prefix) => {
      hits = hits.concat(candidates.filter((r) => isCdaCode(rowCode(r)) && rowCode(r).indexOf(prefix) === 0
        && !carvedOut(rowCode(r))));
    });
  }
  if (!hits.length) hits = candidates.filter((r) => matchesName(line, r));
  return hits;
}

/**
 * The row that answers `line`, or null. A plan that covers ANY code in the
 * line covers the line, so covered rows are tried first, then the rest of the
 * answered rows, then the ones the carrier returned nothing for. `opts.age` is
 * the patient's age in years, for lines coded by dentition.
 */
export function matchLineRow(rows, line, opts) {
  const all = (rows || []).filter(Boolean).slice().sort(compareByCode);
  const tiers = [all.filter(isCoveredRow), all.filter((r) => !r.data_unavailable), all];
  for (const tier of tiers) {
    const hits = hitsIn(tier, line, opts);
    if (!hits.length) continue;
    if (line.pick === "widest") {
      return hits.slice().sort((a, b) => (Number(b.max_occurrences) || 0) - (Number(a.max_occurrences) || 0))[0];
    }
    return hits[0];
  }
  return null;
}

// Every code the table knows, named once — so the same code reads the same on
// every line it answers ("23325 composite, permanent molar, 5 surfaces" under
// Restorative and under Composites on Molars alike).
export const CODE_NAMES = {};
PROCEDURE_LINES.forEach((l) => (l.codes || []).forEach((x) => { if (!CODE_NAMES[x.code]) CODE_NAMES[x.code] = x.name; }));

/** The short name for the code that answered a line — from the table, else the PMS description. */
export function answeredName(line, row) {
  const code = rowCode(row);
  const known = ((line && line.codes) || []).find((x) => x.code === code);
  if (known) return known.name;
  if (CODE_NAMES[code]) return CODE_NAMES[code];
  const desc = rowDescription(row).replace(/\s+/g, " ").trim();
  if (!desc) return null;
  if (desc.length <= 40) return desc;
  const cut = desc.slice(0, 40);
  const at = cut.lastIndexOf(" ");
  return `${(at > 20 ? cut.slice(0, at) : cut).trim()}…`;
}

// ---- naming a breakdown row the way Cleer does ----------------------------------
//
// The Breakdowns table names each row by the line it answers, in Cleer's
// wording, with the code beside it: "Recall exam 01202", "Complete exam, mixed
// teeth 01102". The order is Cleer's checklist (the same 20 lines on every
// Cleer profile), then the lines Cleer covers only with the plan's Basic /
// Major / Ortho percentages. A code belongs to the FIRST line here that claims
// it, so 23222 reads as a white filling on a molar rather than "Fillings", and
// 27205 as part of Implants rather than "Crown".
export const TABLE_LINES = [
  ["polishing", "Polish"],
  ["fluoride", "Fluoride"],
  ["scaling", "Scaling"],
  ["perioScaling", "Root planing"],
  ["recall", "Recall exam"],
  ["emergencyExam", "Emergency exam"],
  ["specificExam", "Specific exam"],
  ["newPatientExam", "Limited new patient exam"],
  ["exam", "Complete exam"],
  ["pan", "Panoramic X-ray"],
  ["bitewings", "Bitewing X-rays"],
  ["ohi", "Oral hygiene instruction (OHI)"],
  ["molarComposites", "White fillings on molars"],
  ["nightGuard", "Night guard"],
  ["ridgePreservation", "PRF (ridge preservation)"],
  ["sealants", "Sealants"],
  ["preventiveResin", "Preventive resin"],
  ["cbct", "CBCT scan"],
  ["nitrous", "Nitrous oxide"],
  ["implants", "Implants"],
  // Not lines on Cleer's checklist; they give the rate for the procedure itself.
  ["fullSeries", "Full-mouth X-rays"],
  ["restorative", "Fillings"],
  ["endodontics", "Root canal"],
  ["perioSurgery", "Perio surgery"],
  ["oralSurgery", "Extraction"],
  ["crown", "Crown"],
  ["bridge", "Bridge"],
  ["denture", "Denture"],
  ["reline", "Denture reline"],
  ["ortho", "Orthodontics"],
].map(([key, name], order) => Object.assign({ name, order }, lineByKey[key]));

// What tells two codes of one line apart, said plainly. Codes not listed need
// nothing beside their line's name.
const QUALIFIERS = {
  "01102": "mixed teeth", "01101": "baby teeth",
  "02141": "1 image", "02142": "2 images", "02143": "3 images", "02144": "4 images",
  "02101": "12+ images", "02102": "16+ images",
  "11101": "1 unit", "11107": "half unit",
  "11111": "1 unit", "11112": "2 units", "11113": "3 units", "11114": "4 units",
  "11115": "5 units", "11116": "6 units", "11117": "half unit",
  "43421": "1 unit", "43422": "2 units", "43423": "3 units", "43424": "4 units",
  "43425": "5 units", "43426": "6 units", "43427": "half unit",
  "12113": "varnish", "12112": "gel or foam", "12111": "rinse", "12101": "topical",
  "13401": "first tooth", "13409": "each additional tooth",
  "13411": "first tooth", "13419": "each additional tooth",
  "14611": "upper", "14612": "lower",
  "23321": "bonded, 1 surface", "23322": "bonded, 2 surfaces", "23323": "bonded, 3 surfaces",
  "23324": "bonded, 4 surfaces", "23325": "bonded, 5 surfaces",
  "23221": "non-bonded, 1 surface", "23222": "non-bonded, 2 surfaces", "23223": "non-bonded, 3 surfaces",
  "23224": "non-bonded, 4 surfaces", "23225": "non-bonded, 5 surfaces",
  "21211": "silver, 1 surface",
  "33111": "1 canal", "33121": "2 canals", "33124": "2 canals, calcified", "33131": "3 canals", "33141": "4+ canals",
  "42431": "flap with bone recontouring", "42421": "flap with curettage",
  "71101": "simple", "72211": "impacted",
  "27211": "porcelain fused to metal", "27201": "porcelain", "27205": "crown on implant",
  "02951": "imaging guide",
  "53201": "partial, upper", "53202": "partial, lower", "51101": "complete, upper", "51102": "complete, lower",
  "56241": "partial", "56211": "complete", "56221": "partial, chairside",
  "93341": "predetermination", "93331": "payment in progress",
  "92412": "2 units", "92413": "3 units", "92414": "4 units", "92416": "6 units",
};

const GENERIC_DESCRIPTION = /^\s*default description\s*$/i;

function tableLineFor(code, description) {
  const exact = TABLE_LINES.find((line) => (line.codes || []).some((x) => x.code === code));
  if (exact) return exact;
  if (isCdaCode(code)) {
    const ranged = TABLE_LINES.find((line) => (line.prefixes || []).some((p) => code.indexOf(p) === 0)
      && !(line.excludePrefixes || []).some((p) => code.indexOf(p) === 0));
    if (ranged) return ranged;
  }
  const row = { procedure_code_details: { procedure_code: code, description } };
  return TABLE_LINES.find((line) => matchesName(line, row)) || null;
}

/**
 * How the Breakdowns table names a row: `name` in Cleer's wording (the
 * office's own description for a code no line claims), a `qualifier` when the
 * line has several codes, the `code`, and the office's full `description` for
 * the hover. `order` sorts rows into Cleer's checklist order.
 */
export function procedureName(row) {
  const code = rowCode(row);
  const description = rowDescription(row);
  const line = tableLineFor(code, description);
  if (!line) {
    return {
      line: null, code, description, qualifier: "", order: TABLE_LINES.length,
      name: description && !GENERIC_DESCRIPTION.test(description) ? description : "",
    };
  }
  return { line, code, description, name: line.name, qualifier: QUALIFIERS[code] || "", order: line.order };
}

/** Rows in Cleer's order: by line, then the line's preferred code first, then by code. */
export function compareTableRows(a, b) {
  const na = procedureName(a);
  const nb = procedureName(b);
  if (na.order !== nb.order) return na.order - nb.order;
  const rank = (n) => {
    const i = n.line ? (n.line.codes || []).findIndex((x) => x.code === n.code) : -1;
    return i === -1 ? 999 : i;
  };
  return rank(na) - rank(nb) || compareByCode(a, b);
}

/**
 * Cleer's "Scaling and root planing combined?", read from the line's shared
 * limit: a scaling line that shares its limit with a root planing code (or the
 * other way round). Returns the other procedure's name, or "".
 */
export function combinedWith(row) {
  const code = rowCode(row);
  const shared = Array.isArray(row && row.shared_limit_codes) ? row.shared_limit_codes.map(String) : [];
  // Sun Life lists the sharing procedures by NAME ("DEEP CLEANING -15 MINUTES"
  // is root planing), the other paths by code.
  const rootPlaning = (s) => s.indexOf("434") === 0 || /deep cleaning|root planing|periodontal scaling/i.test(s);
  const scaling = (s) => s.indexOf("111") === 0 || (/\bscaling\b/i.test(s) && !/periodontal/i.test(s));
  if (code.indexOf("111") === 0 && shared.some(rootPlaning)) return "root planing";
  if (code.indexOf("434") === 0 && shared.some(scaling)) return "scaling";
  return "";
}
